import {
    Button,
    Checkbox,
    Modal,
    ModalHeader,
    SalutationAndTitleInputs,
    Select,
    ValidatingInput,
    ValidationForm,
    ValidationSummary,
} from '@ygtechnologies/bygr-frontend-component-library';

import React, { useEffect, useState } from 'react';
import { BygrProfileDetails } from 'types';
import { useLocalStorage } from 'utils/hooks';
import { ProfileInvitationOptions, inviteProfileMember } from 'services/profile-invitations';

interface InviteProfileMemberModalProps {
    isOpen: boolean;
    toggle(): void;
    onSave(): void;
    profile: BygrProfileDetails;
}

const SupportedCultures = [
    {
        locale: 'en-GB',
        englishName: 'English',
    },
    {
        locale: 'nb-NO',
        englishName: 'Norwegian',
    },
    {
        locale: 'he-IL',
        englishName: 'Hebrew',
    },
    {
        locale: 'sv-SE',
        englishName: 'Swedish',
    },
    {
        locale: 'de-DE',
        englishName: 'German',
    },
];

const languageOptions = SupportedCultures.map((culture) => ({ label: culture.englishName, value: culture.locale }));

const InviteProfileMemberModal: React.FC<InviteProfileMemberModalProps> = ({ isOpen, toggle, onSave, profile }) => {
    const [language, setLanguage] = useLocalStorage('profileInviteLanguage', SupportedCultures[0].locale);

    const [inviteOptions, setInviteOptions] = useState<Omit<ProfileInvitationOptions, 'language'>>({
        firstName: '',
        lastName: '',
        email: '',
        profileId: profile.id,
        phoneNumber: '',
        inviteeSalutation: null,
        inviteeTitle: null,
        isAdministrator: false,
        hasChangeCatalogPermission: false,
        countryCode: profile.countryCode,
    });

    const [isSaving, setIsSaving] = useState(false);

    const handleInviteProfile = async () => {
        setIsSaving(true);
        await inviteProfileMember({ ...inviteOptions, language });
        setIsSaving(false);
        onSave();
        toggle();
    };

    const reset = () => {
        setIsSaving(false);
        setInviteOptions({
            firstName: '',
            lastName: '',
            email: '',
            inviteeSalutation: null,
            inviteeTitle: null,
            profileId: profile.id,
            phoneNumber: '',
            isAdministrator: false,
            hasChangeCatalogPermission: false,
            countryCode: profile.countryCode,
        });
    };

    return (
        <Modal toggle={toggle} onClose={reset} isOpen={isOpen}>
            <ModalHeader>Add user</ModalHeader>
            <ValidationForm onSubmit={handleInviteProfile} className="form columns-1">
                <label>
                    Email
                    <ValidatingInput
                        required
                        type="email"
                        autoComplete="off"
                        className="form-control"
                        placeholder={'Email'}
                        value={inviteOptions.email}
                        onChange={(e) => setInviteOptions({ ...inviteOptions, email: e.target.value })}
                    />
                </label>

                <SalutationAndTitleInputs
                    // countryCode={profile.countryCode}
                    value={inviteOptions}
                    onChange={(changes) => setInviteOptions({ ...inviteOptions, ...changes })}
                />

                <label>
                    First name
                    <ValidatingInput
                        type="text"
                        autoComplete="off"
                        className="form-control"
                        placeholder={'First name'}
                        value={inviteOptions.firstName}
                        onChange={(e) => setInviteOptions({ ...inviteOptions, firstName: e.target.value })}
                    />
                </label>

                <label>
                    Last name
                    <ValidatingInput
                        type="text"
                        autoComplete="off"
                        className="form-control"
                        placeholder={'Last name'}
                        value={inviteOptions.lastName}
                        onChange={(e) => setInviteOptions({ ...inviteOptions, lastName: e.target.value })}
                    />
                </label>
                <label>
                    Email language
                    <Select
                        value={languageOptions.find((x) => x.value === language)}
                        options={languageOptions}
                        onChange={(e: any) => setLanguage(e!.value)}
                    />
                </label>

                <Checkbox
                    onChange={(e) => setInviteOptions({ ...inviteOptions, isAdministrator: e.target.checked })}
                    checked={inviteOptions.isAdministrator}>
                    Invite as administrator
                </Checkbox>

                <Checkbox
                    disabled={inviteOptions.isAdministrator}
                    onChange={(e) => setInviteOptions({ ...inviteOptions, hasChangeCatalogPermission: e.target.checked })}
                    checked={inviteOptions.hasChangeCatalogPermission}>
                    Has change catalog permission
                </Checkbox>

                {/* <div className="form-label">
                    <Trans>Permissions</Trans>
                    <div>
                        <ProfilePermissionsEditor value={permissions} onChange={setPermissions} />
                    </div>
                </div> */}

                <div className="label-offset">
                    <ValidationSummary />
                </div>

                <div className="buttons right">
                    <Button color="tertiary" onClick={toggle}>
                        Cancel
                    </Button>
                    <Button type="submit" color="primary" loading={isSaving}>
                        Invite user
                    </Button>
                </div>
            </ValidationForm>
        </Modal>
    );
};

export default InviteProfileMemberModal;
